<template>
    <b-modal
        id="magazine-approval-wizard"
        v-model="modal"
        ref="magazineApprovalWizard"
        modal-class="apprentice-default-modal"
        :ok-title="$t('common.close')"
        ok-variant="primary"
        size="md"
        centered
        :title="$t('magazine.add_existing_user')"
        hide-footer
        hide-header
        :no-close-on-backdrop="true">
        <design-2021-simple-multi-steps-form-component
            class="approval-multi-step"
            ref="multi-step-form"
            :steps="steps"
            :start-step-index="initShow ? 1 : 0"
            @validate-step="validateStep"
            @submit-form="onOk"
            :hide-footer="true">
            <template #step1>
                <div class="approval-step">
                    <i class="fal fa-fw icon fa-exclamation-circle"></i>
                    <h1 class="title">{{ $t('magazine.approve_all_pages') }}</h1>
                    <p class="short-description">{{ $t('magazine.are_you_sure_you_want_to_approve_all_pages') }}</p>
                    <div class="actions">
                        <b-button
                            variant="outline-primary"
                            @click="hideModal">
                            {{ $t('common.cancel') }}
                        </b-button>
                        <b-button
                            variant="primary"
                            @click="approveAllPages">
                            {{ $t('magazine.yes_approve') }}
                        </b-button>
                    </div>
                </div>
            </template>

            <template #step2>
                <div class="congratulations">
                    <h2 class="title">{{ $t('magazine.congratulations') }}</h2>
                    <p class="intro">{{ $t('magazine.you_created_a_beautiful_yearbook') }}</p>
                    <p
                        class="description"
                        v-html="
                            $t(
                                'magazine.before_we_get_started_we_would_like_to_check_a_few_things_with_you_and_we_would_like_to_send_you_a_thank_you_note_for_the_great_cooperation',
                            )
                        "></p>
                    <p class="info-notice">
                        {{ $t('magazine.click_on_next_to_finalize_the_details_and_provide_your_address') }}
                    </p>
                    <div class="actions">
                        <b-button
                            class="btn-continue"
                            variant="primary"
                            @click="next">
                            {{ $t('common.next') }}
                        </b-button>
                    </div>
                </div>
            </template>

            <template #step3>
                <div class="contact-choice">
                    <p>
                        {{
                            $t('magazine.your_work_for_the_yearbook_is_over_we_will_now_take_it_over_from_you', {
                                season: currentSeason.season,
                            })
                        }}
                    </p>

                    <p>
                        {{
                            $t(
                                'magazine.during_the_summer_holidays_we_will_prepare_all_the_new_yearbooks_again_will_you_be_our_contact_person_next_year',
                            )
                        }}
                    </p>

                    <div class="actions">
                        <b-button
                            variant="outline-primary"
                            @click="openNewContact">
                            {{ $t('common.no') }}
                        </b-button>
                        <b-button
                            variant="primary"
                            @click="next">
                            {{ $t('common.yes') }}
                        </b-button>
                    </div>
                </div>
            </template>

            <template #step4>
                <div
                    class="contact-person-choice"
                    v-if="!sameContactPerson">
                    <div class="new-contact-person">
                        <div
                            class="errors"
                            v-if="formHasError">
                            <b-alert
                                show
                                variant="danger">
                                {{ $t('magazine.not_all_mandatory_fields_are_filled_in_correctly') }}
                            </b-alert>
                        </div>

                        <h5 class="sub-title">
                            {{ $t('magazine.who_will_be_the_contact_person_for_the_year_book_next_year') }}
                        </h5>
                        <b-form-group
                            class="name-form-group"
                            :label="$t('common.name')"
                            label-for="new-contact-name-input">
                            <template v-slot:label> {{ $t('common.name') }} <span class="required">*</span></template>
                            <b-form-input
                                class="new-contact-name"
                                id="new-contact-name-input"
                                v-model="newContactName"
                                type="text"
                                :placeholder="$t('common.name')"
                                :disabled="isContactPersonUnknown">
                            </b-form-input>
                        </b-form-group>

                        <b-form-group
                            class="email-form-group"
                            label="Email:"
                            label-for="new-contact-email-input">
                            <template v-slot:label> Email: <span class="required">*</span></template>
                            <b-form-input
                                class="new-contact-email"
                                id="new-contact-email-input"
                                v-model="newContactEmail"
                                type="text"
                                placeholder="voorbeeld@apprenticexm.com"
                                @keyup.enter="next"
                                :disabled="isContactPersonUnknown">
                            </b-form-input>
                        </b-form-group>

                        <b-form-group
                            class="unknown-contact-form-group"
                            :label="null"
                            label-for="unknown-contact-checkbox">
                            <b-form-checkbox
                                class="apprentice-checkbox unknown-contact-person-checkbox"
                                id="unknown-contact-checkbox"
                                v-model="isContactPersonUnknown"
                                name="checkbox-1"
                                :value="true"
                                :unchecked-value="false">
                                {{ $t('magazine.not_familiar_yet') }}
                            </b-form-checkbox>
                        </b-form-group>
                    </div>

                    <div class="actions">
                        <b-button
                            variant="primary"
                            @click="next">
                            {{ !sameContactPerson ? $t('common.next') : $t('magazine.sent') }}
                        </b-button>
                    </div>
                </div>
                <div
                    class="finished"
                    v-else
                >
                    <i class="fal fa-fw icon fa-check-circle"></i>
                    <h2 class="title">{{ $t('magazine.thank_you_we_wish_you_a_very_nice_summer_holiday') }}</h2>
                    <div class="status-progress">
                        <div class="status-1">
                            <img
                                src="/images/magazine-wizard/technical-test.png"
                                :alt="$t('magazine.technical_check')" />
                            <p>{{ $t('magazine.we_will_start_with_a_technical_check') }}</p>
                        </div>

                        <div class="status-2">
                            <img
                                src="/images/magazine-wizard/printpress.png"
                                :alt="$t('magazine.the_book_goes_into_our_printing_press')" />
                            <p>{{ $t('magazine.the_book_goes_into_our_printing_press') }}.</p>
                        </div>

                        <div class="status-3">
                            <img
                                src="/images/magazine-wizard/transport.png"
                                :alt="$t('magazine.the_books_will_be_sent')" />
                            <p>{{ $t('magazine.the_books_will_be_sent') }}</p>
                        </div>

                        <div class="status-4">
                            <img
                                src="/images/magazine-wizard/delivery.png"
                                :alt="$t('magazine.books_will_be_delivered')" />
                            <i18n
                                path="magazine.the_books_will_be_delivered_at_date"
                                tag="p">
                                <template v-slot:date>
                                    <span class="delivery-date">{{ formatDeadline() }}</span>
                                </template>
                            </i18n>
                            <p>
                                De boeken worden <span class="delivery-date">{{ formatDeadline() }}</span> geleverd.
                            </p>
                        </div>

                        <div class="status-5">
                            <img
                                src="/images/magazine-wizard/contact.png"
                                alt="Contact" />
                            <p>{{ $t('magazine.we_will_contact_you') }}</p>
                        </div>
                    </div>

                    <div class="actions">
                        <b-button
                            variant="primary"
                            @click="closeAndRefresh">
                            {{ $t('common.close') }}
                        </b-button>
                    </div>
                </div>
            </template>

            <template #step5>
                <div
                    class="finished"
                    >
                    <i class="fal fa-fw icon fa-check-circle"></i>
                    <h2 class="title">{{ $t('magazine.thank_you_we_wish_you_a_very_nice_summer_holiday') }}</h2>
                    <div class="status-progress">
                        <div class="status-1">
                            <img
                                src="/images/magazine-wizard/technical-test.png"
                                :alt="$t('magazine.technical_check')" />
                            <p>{{ $t('magazine.we_will_start_with_a_technical_check') }}</p>
                        </div>

                        <div class="status-2">
                            <img
                                src="/images/magazine-wizard/printpress.png"
                                :alt="$t('magazine.the_book_goes_into_our_printing_press')" />
                            <p>{{ $t('magazine.the_book_goes_into_our_printing_press') }}.</p>
                        </div>

                        <div class="status-3">
                            <img
                                src="/images/magazine-wizard/transport.png"
                                :alt="$t('magazine.the_books_will_be_sent')" />
                            <p>{{ $t('magazine.the_books_will_be_sent') }}</p>
                        </div>

                        <div class="status-4">
                            <img
                                src="/images/magazine-wizard/delivery.png"
                                :alt="$t('magazine.books_will_be_delivered')" />
                            <i18n
                                path="magazine.the_books_will_be_delivered_at_date"
                                tag="p">
                                <template v-slot:date>
                                    <span class="delivery-date">{{ formatDeadline() }}</span>
                                </template>
                            </i18n>
                            <p>
                                De boeken worden <span class="delivery-date">{{ formatDeadline() }}</span> geleverd.
                            </p>
                        </div>

                        <div class="status-5">
                            <img
                                src="/images/magazine-wizard/contact.png"
                                alt="Contact" />
                            <p>{{ $t('magazine.we_will_contact_you') }}</p>
                        </div>
                    </div>

                    <div class="actions">
                        <b-button
                            variant="primary"
                            @click="closeAndRefresh">
                            {{ $t('common.close') }}
                        </b-button>
                    </div>
                </div>
            </template>
        </design-2021-simple-multi-steps-form-component>
    </b-modal>
</template>

<script>
export default {
    name: 'design-2021-magazine-approval-wizard-modal-component',
    props: {
        magazineIn: {
            type: Object,
            required: true,
        },
        currentSeason: {
            type: Object,
            required: true,
        },

        initShow: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            modal: this.initShow,
            activeStepIndex: 0,
            sameContactPerson: true,
            newContactName: '',
            newContactEmail: '',
            steps: [
                { step_no: 1, step_valid: true, step_skip: false },
                { step_no: 2, step_valid: true, step_skip: false },
                { step_no: 3, step_valid: false, step_skip: false },
                { step_no: 4, step_valid: false, step_skip: false },
            ],
            formHasError: false,
            isContactPersonUnknown: false,
        };
    },
    methods: {
        next() {
            this.$refs['multi-step-form'].nextStep();
        },
        show() {
            this.modal = true;
        },

        hideModal() {
            this.$refs.magazineApprovalWizard.hide();
        },

        validateStep(step) {
            this.formHasError = false;

            switch (step) {
                case 0:
                    // nadat alle pagina's goedgekeurd worden.
                    this.alert.saving();
                    this.axios.get(this.routes.magazineApproveAllPages(this.magazineIn.id)).then(
                        (response) => {
                            this.alert.success();
                        },
                        (error) => {
                            this.alert.ajaxError(error);
                        },
                    );
                    break;

                case 2:
                    if (!this.sameContactPerson) {
                        this.steps[step].step_valid = true;
                        this.next();
                    }
                    else {
                        this.onOk();
                        this.steps[step].step_valid = true;
                        this.next();
                        let modalEl = document.getElementsByClassName('modal-dialog');
                        modalEl[0].classList.add('modal-lg');
                        modalEl[0].classList.remove('modal-md');
                    }
                    break;

                case 3:
                    if (this.sameContactPerson) {
                        if (!this.newContactPersonFormHasError()) {
                            this.steps[step].step_valid = true;
                            this.next();
                        }
                    }
                    else {
                        this.onOk();
                        this.steps[step].step_valid = true;
                        this.next();
                        let modalEl = document.getElementsByClassName('modal-dialog');
                        modalEl[0].classList.add('modal-lg');
                        modalEl[0].classList.remove('modal-md');
                    }
                    break;

                default:
                    break;
            }
        },

        async onOk() {
            let data = {
                same_contact_person: this.sameContactPerson,
            };

            if (!this.sameContactPerson && !this.isContactPersonUnknown) {
                data.new_contact_name = this.newContactName;
                data.new_contact_email = this.newContactEmail;
            }

            this.alert.saving();

            await this.axios.post(this.routes.magazineSubmitContactInfo(this.magazineIn.id), data).then(
                (response) => {
                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        async approveAllPages() {
            this.alert.saving();
            await this.axios.get(this.routes.magazineApproveAllPages(this.magazineIn.id)).then(
                (response) => {
                    this.alert.success();
                    this.next();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        openNewContact() {
            this.steps.push({ step_no: 6, step_valid: true, step_skip: false });
            this.sameContactPerson = false;
            this.next();
        },

        newContactPersonFormHasError() {
            if (
                (this.newContactName === '' ||
                    this.newContactEmail === '' ||
                    !this.$options.filters.validate_email(this.newContactEmail)) &&
                !this.isContactPersonUnknown
            ) {
                this.formHasError = true;
                return true;
            }

            return false;
        },

        // TODO: Use https://kazupon.github.io/vue-i18n/guide/datetime.html to format the date
        formatDeadline() {
            if (this.magazineIn.delivery_date) {
                return (
                    this.$t('magazine.for') +
                    this.$options.filters.date_named_month_short(this.magazineIn.delivery_date)
                );
            } else if (this.magazineIn.deadline && this.magazineIn.deadline.delivery_date) {
                return (
                    this.$t('magazine.for') +
                    this.$options.filters.date_named_month_short(this.magazineIn.deadline.delivery_date)
                );
            } else if (this.magazineIn.editorial_deadline_date) {
                return (
                    this.$t('magazine.for') +
                    this.$options.filters.date_named_month_short(this.magazineIn.editorial_deadline_date)
                );
            } else if (this.magazineIn.customdeadline) {
                return (
                    this.$t('magazine.for') +
                    this.$options.filters.date_named_month_short(this.magazineIn.customdeadline)
                );
            } else {
                return this.$t('magazine.soon');
            }
        },

        closeAndRefresh() {
            this.hideModal();
            location.reload();
        },
    },
};
</script>

<style scoped lang="scss">
p {
    font-size: $base-font-size;

    &:last-child {
        margin-bottom: 0;
    }
}

.title {
    font-size: 1.6875rem;
    font-weight: 600;
}

.sub-title {
    margin-bottom: $grid-gutter-width;
    font-weight: $font-weight-normal;
}

.actions {
    display: flex;
    align-items: center;
    justify-content: center;
    column-gap: map-get($grid-gutter-spacing, 'fifteen');
    margin-top: $grid-gutter-width;
}

.approval-step,
.finished {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    row-gap: map-get($grid-gutter-spacing, 'fifteen');

    .icon {
        color: $warning;
        font-size: 5.5rem;
    }
}

.approval-step {
    .icon,
    .title,
    p {
        margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
    }
}

.congratulations {
    * {
        z-index: 80;
        position: relative;
    }

    p {
        margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
    }

    &:before,
    &:after {
        content: '';
        position: absolute;
        display: block;
        inset: 0;
        height: 100%;
        width: 100%;
        border-radius: 12px;
    }

    &:before {
        background-image: url('https://pa1.aminoapps.com/6794/35e50bce36e3217a558551825013597b8905dbaf_00.gif');
        opacity: 0.7;
    }

    &.congratulations:after {
        background: linear-gradient(
            180deg,
            rgba($white, 0) 20%,
            rgba($white, 0.6810925053615196) 35%,
            rgba($white, 1) 75%
        );
        z-index: 50;
    }
}

.contact-person-choice :deep {
    .unknown-contact-person-checkbox {
        input {
            height: 5px;
            width: 25px;
        }

        .custom-control-label {
            padding-left: map-get($grid-gutter-spacing, 'eight');
            font-size: $base-font-size;

            &:before {
                font-size: 1.6625rem;
                top: -3px;
                width: auto;
                height: auto;
            }
        }
    }
}

.finished {
    .title {
        text-align: center;
    }

    .icon {
        color: $success;
    }

    .status-progress {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: center;
        column-gap: map-get($grid-gutter-spacing, 'twenty-one');
        margin-top: map-get($grid-gutter-spacing, 'twenty-one');

        div[class^='status'] {
            position: relative;
            background-color: $white;
            z-index: 20;
            width: 100px;
            height: 170px;

            p {
                font-size: 0.6875rem;
            }

            &:not(:last-child):after {
                content: '';
                border-bottom: 5px dotted $text-color;
                display: block;
                position: absolute;
                width: 30px;
                top: 40px;
                left: 84px;
            }
        }

        img {
            width: 80px;
            height: 80px;
            object-fit: contain;
            object-position: center;
        }

        .delivery-date {
            font-size: 0.75rem;
            font-weight: 700;
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'lg')) {
    .finished {
        .title {
            text-align: left;
        }

        .status-progress {
            flex-wrap: nowrap;
            align-items: center;
            justify-content: flex-start;
            column-gap: map-get($grid-gutter-spacing, 'sixty');

            div[class^='status'] {
                &:not(:last-child):after {
                    border-bottom: 8px dotted $text-color;
                    width: 70px;
                }
            }
        }
    }
}
</style>
